import { Injectable } from '@angular/core';
import { RecentProductState } from '@epicuro-next/navigation/main-navigation';
import {
  NAMESPACE_SEARCH_SUGGEST,
  SearchSuggestConfigurationKeys,
  SearchSuggestConfigurations,
  searchSuggestConfigurationsDefaultValues,
  selectConfigurationValueFrom,
  selectSearchSuggestEnabled,
} from '@epicuro-next/platform/configuration';
import { LocalStorageService } from '@epicuro-next/platform/storage';
import {
  ExtendedFilterQueryParams,
  ProductBasketService,
  selectCollapse,
  selectGroupByVariantAvailable,
  selectPresetValue,
  selectProductBasketQueryParamsExtended,
  selectProductBasketSearchQuery,
  selectSearchSuggestDefaultProductBasketQueryParams,
  selectSortDirection,
  selectSortField,
} from '@epicuro-next/state/p-basket-state';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import {
  isEqual,
  transformFilterQueryParamToSearchQuery,
} from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import {
  Product,
  SearchQuery,
  SearchSuggestResult,
} from 'graphql-api/gql-api-interfaces';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { SearchStorageKeys } from '../models/search-storage-keys';

import { getRecentProduct } from './search-bar-helpers';

interface SearchHistoryStoreState {
  [key: string]: string[];
}

interface SearchComponentStoreState {
  searchHistory: SearchHistoryStoreState;
  recentProducts: RecentProductState[];
  query: string | null;
  products: Product[];
  isLoading: boolean;
  didQuickSearch: boolean;
  error?: boolean | null;
  searchSuggestResult: SearchSuggestResult | null;
  searchSuggestQuery: string | null;
}

@Injectable()
export class SearchBarComponentStore extends ComponentStore<SearchComponentStoreState> {
  constructor(
    private localStorage: LocalStorageService,
    private store: Store,
    private productBasketService: ProductBasketService,
  ) {
    const searchHistory = localStorage.get(
      SearchStorageKeys.SEARCH_QUERIES_STORE_KEY,
    );
    const recentProducts = localStorage.get(
      SearchStorageKeys.SEARCH_RECENT_PRODUCTS_STORE_KEY,
    );

    super({
      searchHistory: searchHistory ?? {},
      recentProducts: recentProducts ?? [],
      query: null,
      products: [],
      isLoading: false,
      didQuickSearch: false,
      searchSuggestResult: null,
      searchSuggestQuery: null,
    });

    this.store
      .pipe(select(selectProductBasketSearchQuery), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.patchState({
          query: value,
        });
      });
  }

  public readonly resetOtherFiltersOnSuggest$ = this.select(
    this.store.select(
      selectConfigurationValueFrom<
        SearchSuggestConfigurationKeys.SkipResetOtherFilters,
        SearchSuggestConfigurations
      >(
        NAMESPACE_SEARCH_SUGGEST,
        SearchSuggestConfigurationKeys.SkipResetOtherFilters,
        searchSuggestConfigurationsDefaultValues[
          SearchSuggestConfigurationKeys.SkipResetOtherFilters
        ],
      ),
    ),
    (value) => !value,
  );

  private readonly searchSuggestQuery$ = this.select(
    this.store.select(selectProductBasketQueryParamsExtended),
    this.store.select(selectSearchSuggestDefaultProductBasketQueryParams),
    this.store.select(
      selectConfigurationValueFrom<
        SearchSuggestConfigurationKeys.SuggestedProductsCount,
        SearchSuggestConfigurations
      >(
        NAMESPACE_SEARCH_SUGGEST,
        SearchSuggestConfigurationKeys.SuggestedProductsCount,
        searchSuggestConfigurationsDefaultValues[
          SearchSuggestConfigurationKeys.SuggestedProductsCount
        ],
      ),
    ),
    this.store.select(
      selectConfigurationValueFrom<
        SearchSuggestConfigurationKeys.ApplyFiltersOnRequest,
        SearchSuggestConfigurations
      >(
        NAMESPACE_SEARCH_SUGGEST,
        SearchSuggestConfigurationKeys.ApplyFiltersOnRequest,
        searchSuggestConfigurationsDefaultValues[
          SearchSuggestConfigurationKeys.ApplyFiltersOnRequest
        ],
      ),
    ),
    (fullParams, defaultParams, num, applyFilters) => {
      const suggestParams = transformFilterQueryParamToSearchQuery(
        applyFilters ? fullParams : defaultParams,
      );
      return {
        ...suggestParams,
        num: num ?? 5,
        start: 0,
      };
    },
  );
  public isSearchSuggestEnabled$ = this.store.pipe(
    select(selectSearchSuggestEnabled),
  );
  public readonly searchSuggestResult$ = this.select(
    (state) => state.searchSuggestResult,
  );
  public searchHistory$ = this.select((state) => state.searchHistory);
  public recentProducts$ = this.select((state) => state.recentProducts);
  public query$ = this.select((state) => state.query);
  public products$ = this.select((state) => state.products);
  public didQuickSearch$ = this.select((state) => state.didQuickSearch);
  public isLoading$ = this.select((state) => state.isLoading);
  public error$ = this.select((state) => state.error);
  public siteId$ = this.store.pipe(select(selectSiteId));
  public subSiteId$ = this.store.pipe(select(selectSubSiteId));

  public setQuery = this.effect((query$: Observable<string | null>) => {
    return query$.pipe(
      tap((query: string | null) => {
        this.patchState({ query });
        if (query) {
          this.writeSearch(query);
        }
      }),
    );
  });

  public removeFromHistory = this.effect((text$: Observable<string>) => {
    return text$.pipe(
      concatLatestFrom(() => [
        this.searchHistory$,
        this.siteId$,
        this.subSiteId$,
      ]),
      tap(
        ([text, history, siteId, subSiteId]: [
          string,
          SearchHistoryStoreState,
          string,
          string,
        ]) => {
          const siteKey = this.getSiteKey(siteId, subSiteId);
          const index = history[siteKey].findIndex((item) => item === text);

          if (index < 0) {
            return;
          }

          history[siteKey].splice(index, 1);
          this.localStorage.set(
            SearchStorageKeys.SEARCH_QUERIES_STORE_KEY,
            history,
          );
          this.patchState({
            searchHistory: history,
          });
        },
      ),
    );
  });

  private writeSearch = this.effect((query$: Observable<string>) => {
    return query$.pipe(
      concatLatestFrom(() => [
        this.searchHistory$,
        this.siteId$,
        this.subSiteId$,
      ]),
      filter(
        ([query, queries, siteId, subSiteId]: [
          string,
          SearchHistoryStoreState,
          string,
          string,
        ]) => {
          const siteKey = this.getSiteKey(siteId, subSiteId);

          return (
            !queries[siteKey] ||
            queries[siteKey]?.findIndex((item) => item === query) < 0
          );
        },
      ),
      tap(
        ([query, queries, siteId, subSiteId]: [
          string,
          SearchHistoryStoreState,
          string,
          string,
        ]) => {
          const siteKey = this.getSiteKey(siteId, subSiteId);

          if (!queries[siteKey]) {
            queries[siteKey] = [query];
          } else {
            queries[siteKey].unshift(query);

            if (queries[siteKey].length > 8) {
              queries[siteKey].pop();
            }
          }

          this.localStorage.set(
            SearchStorageKeys.SEARCH_QUERIES_STORE_KEY,
            queries,
          );
          this.patchState({
            searchHistory: queries,
          });
        },
      ),
    );
  });

  public clearProductsOnFilterChange = this.effect<ExtendedFilterQueryParams>(
    (text$) => {
      return text$.pipe(
        map(({ q, num, start, ...params }) => params),
        distinctUntilChanged(isEqual),
        tap(() => {
          this.patchState({
            products: [],
            didQuickSearch: false,
            searchSuggestResult: null,
          });
        }),
      );
    },
  )(this.store.select(selectProductBasketQueryParamsExtended));

  public loadProducts = this.effect((query$: Observable<string>) => {
    return query$.pipe(
      tap(() => this.patchState({ isLoading: true })),
      concatLatestFrom(() => [
        this.store.select(selectPresetValue),
        this.store.select(selectGroupByVariantAvailable),
        this.store.select(selectCollapse),
        this.store.select(selectSubSiteId),
        this.store.select(selectSortField),
        this.store.select(selectSortDirection),
        this.store.select(selectProductBasketQueryParamsExtended),
      ]),
      switchMap(
        ([
          query,
          preset,
          groupByVariants,
          collapse,
          subsite,
          sortField,
          sortDirection,
          params,
        ]) => {
          const defaultParams = {
            q: query,
            num: 24,
            start: 0,
            preset,
            sortDirection,
            sortField,
            withRelatedVariants: groupByVariants,
            subsites: subsite ? [subsite] : undefined,
            collapse,
          };
          const isInPBasket = window.location.href.includes('browse/search');
          const pBasketParams = {
            ...(transformFilterQueryParamToSearchQuery({
              ...params,
              q: query,
              preset,
            }) as SearchQuery),
          };
          return this.productBasketService
            .getProducts(
              {
                ...(isInPBasket ? pBasketParams : defaultParams),
                num: 24,
                start: 0,
                useV2Search: true,
              },
              false,
            )
            .pipe(
              tapResponse(
                (res) => {
                  this.patchState({
                    isLoading: false,
                    products: res.products as Product[],
                    didQuickSearch: true,
                  });
                },
                (err) => {
                  if (err) {
                    this.patchState({
                      isLoading: false,
                      error: true,
                    });
                  }
                },
              ),
            );
        },
      ),
    );
  });

  public loadSuggestProducts = this.effect((query$: Observable<string>) => {
    return query$.pipe(
      concatLatestFrom(() => this.isSearchSuggestEnabled$),
      filter(
        ([query, searchSuggestEnabled]) => !!(query && searchSuggestEnabled),
      ),
      map(([query]) => query),
      tap(() => this.patchState({ isLoading: true })),
      concatLatestFrom(() => this.searchSuggestQuery$),
      switchMap(([query, params]) => {
        return this.productBasketService
          .getSuggestProducts(
            {
              ...params,
              q: query,
            },
            false,
          )
          .pipe(
            tapResponse(
              (res) => {
                this.patchState({
                  isLoading: false,
                  searchSuggestResult: res,
                });
              },
              // tslint:disable-next-line:no-identical-functions
              (err) => {
                if (err) {
                  this.patchState({
                    isLoading: false,
                    error: true,
                  });
                }
              },
            ),
          );
      }),
    );
  });

  public writeRecentProduct(product: Product) {
    const recentProduct = getRecentProduct(product);

    this.recentProducts$.pipe(take(1)).subscribe((products) => {
      const isDuplicate = products
        .map((pr) => pr.id)
        .includes(product.productId);
      if (!isDuplicate) {
        this.storeRecentProductToStorage(recentProduct);
      }
      this.patchState({
        recentProducts: !isDuplicate
          ? [...products, recentProduct]
          : [...products],
      });
    });
  }

  private storeRecentProductToStorage(product: RecentProductState) {
    const products =
      this.localStorage.get(
        SearchStorageKeys.SEARCH_RECENT_PRODUCTS_STORE_KEY,
      ) ?? [];

    this.localStorage.set(SearchStorageKeys.SEARCH_RECENT_PRODUCTS_STORE_KEY, [
      ...products,
      product,
    ]);
  }

  public getSiteKey(siteId: string | null, subSiteId: string | null): string {
    return subSiteId ? `${siteId ?? ''}:${subSiteId ?? ''}` : siteId ?? '';
  }
}
